import { createMutation } from '@hum/api/src/utils/createMutation';
import {
  Connector,
  CreateConnectUrl,
  ConnectUrlResponse,
  DetailedConnectorRequestForm,
  DetailedConnectorRequestFormFields,
  DeleteConnectorPayload,
  DETAILED_CONNECTOR_REQUEST_FORM_BASE_URL,
  FileOutput,
  CompanyFile,
  MutationFileInput,
  FileStatus,
} from '@hum/types';
import { getConnectors } from './queries';
import { createApiClient } from '@hum/api/src/utils';
import { toast } from '@hum/common/src/modules/toast';

export type CreateConnector = {
  connectorConfigId: number;
  parameters: Record<string, any>; // for capital connectors
};

export const createConnector = createMutation<Connector, CreateConnector>(
  ({ api, payload, router, session }) => {
    const companyId = session.user?.isAdmin
      ? router.query.company_id
      : session.user?.organizationId;

    return api.post(`/v2/companies/${companyId}/connectors`, {
      connector_config_id: payload.connectorConfigId,
      parameters: payload.parameters,
    });
  },
  {
    invalidates: [getConnectors],
  }
);

export const createConnectUrl = createMutation<
  ConnectUrlResponse,
  CreateConnectUrl
>(
  ({ api, payload, router, session }) => {
    const companyId = session.user?.isAdmin
      ? router.query.company_id
      : session.user?.organizationId;
    return api.post(`/v2/companies/${companyId}/connectors/connect_url`, {
      connector_config_id: payload.connectorConfigId,
      parameters: payload.parameters,
    });
  },
  { invalidates: ['/companies'] }
);

export const disconnectConnector = createMutation<
  Connector,
  DeleteConnectorPayload
>(
  ({ api, payload, session, router }) => {
    const companyId = router.query.company_id || session.user?.organizationId;
    return api.post(
      `/v2/companies/${companyId}/connectors/${payload.connectorId}/disconnect`
    );
  },
  {
    invalidates: [getConnectors, '/companies'],
    errorToast: { message: 'Failed to disconnect connector' },
  }
);

export const deleteConnector = createMutation<
  Connector,
  DeleteConnectorPayload
>(
  ({ api, payload, session, router }) => {
    const isAdmin = session.user?.isAdmin;
    const companyId = isAdmin
      ? router.query.company_id
      : session.user?.organizationId;
    return api.delete(
      `/v2/companies/${companyId}/connectors/${payload.connectorId}`
    );
  },
  {
    errorToast: { message: 'Failed to disconnect connector' },
    successToast: { message: 'Connector successfully disconnected' },
    invalidates: [getConnectors, '/companies'],
  }
);

export const submitDetailedConnectorRequestForm = createMutation<
  void,
  DetailedConnectorRequestForm
>(({ payload, session }) => {
  const client = createApiClient({
    baseURL: DETAILED_CONNECTOR_REQUEST_FORM_BASE_URL,
  });
  const body = new FormData();
  body.append(
    DetailedConnectorRequestFormFields.Environment,
    payload.environment || ''
  );
  body.append(
    DetailedConnectorRequestFormFields.Email,
    session.user?.email || ''
  );
  body.append(DetailedConnectorRequestFormFields.Reason, payload.reason || '');
  if (payload.reason === 'Other') {
    body.append(
      DetailedConnectorRequestFormFields.Connector,
      '__other_option__'
    );
    body.append(
      DetailedConnectorRequestFormFields.Other,
      payload.connector || ''
    );
  } else {
    body.append(
      DetailedConnectorRequestFormFields.Connector,
      payload.connector || ''
    );
  }

  return client.post('/formResponse', body, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
});

export const uploadFile = createMutation<CompanyFile | void, MutationFileInput>(
  async ({ api, session, router, payload }) => {
    const companyId = router.query.company_id || session.user?.organizationId;
    const { blob, ...body } = payload;

    if (!blob) {
      return;
    }

    // post file meta data and get pre-signed url for posting to s3
    const { file, presignedPost }: FileOutput = await api.post(
      `/v2/companies/${companyId}/files`,
      body
    );
    const { fields, url } = presignedPost;
    const form = new FormData();

    // we don't want these in camelCase because the request will fail
    const correctedFields = {
      awsAccessKeyId: 'AWSAccessKeyId',
      contentType: 'Content-Type',
      xAmzSecurityToken: 'x-amz-security-token',
    };

    Object.entries(fields).forEach(([key, value]) => {
      form.append(correctedFields[key] || key, value);
    });
    form.append('file', blob);

    // post file contes to s3
    toast.info('Uploading file...');
    const client = createApiClient({ baseURL: url });
    const updateFileStatus = async (status: FileStatus) =>
      await api.patch(`/v2/companies/${companyId}/files/${file.id}`, {
        status,
      });

    try {
      await client.post('', form);
      toast.success('File uploaded successfully');
      return await updateFileStatus(FileStatus.Success);
    } catch (e) {
      console.error(e);
      toast.error('Failed to upload file.');
      return await updateFileStatus(FileStatus.Failed);
    }
  },
  {
    invalidates: ['/companies', '/files'],
  }
);

export const deleteFile = createMutation<void, { fileId: number }>(
  async ({ api, session, router, payload }) => {
    const companyId = router.query.company_id || session.user?.organizationId;

    return api.delete(`/v2/companies/${companyId}/files/${payload.fileId}`);
  },
  {
    invalidates: ['/companies', '/files'],
    errorToast: { message: 'Failed to delete file' },
  }
);
